/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import { isCallPutOptions } from 'helpers/utils';
import { ContractType, Product } from 'types/IProducts';
import { TickerData } from 'types/Ticker';

import type { TickerState } from './ticker.types';

// Utility: Initialize state updates object with shallow copies
const initializeStateUpdates = (currentState: TickerState): Required<TickerState> => ({
  tickerObj: { ...currentState.tickerObj },
  optionsTickerObj: { ...currentState.optionsTickerObj },
  spotPrice: { ...currentState.spotPrice },
  markPrice: new Map(currentState.markPrice),
  greeks: {
    delta: new Map(currentState.greeks.delta),
    gamma: new Map(currentState.greeks.gamma),
    theta: new Map(currentState.greeks.theta),
    vega: new Map(currentState.greeks.vega),
  },
  askIV: new Map(currentState.askIV),
  bidIV: new Map(currentState.bidIV),
  bidQty: new Map(currentState.bidQty),
  askQty: new Map(currentState.askQty),
  bestAsk: new Map(currentState.bestAsk),
  bestBuy: new Map(currentState.bestBuy),
  impliedVolatility: new Map(currentState.impliedVolatility),
  priceBands: new Map(currentState.priceBands),
  oi: new Map(currentState.oi),
  callOi: new Map(currentState.callOi),
  putOi: new Map(currentState.putOi),
  markIV: new Map(currentState.markIV),
  oiChange: new Map(currentState.oiChange),
  callTurnoverUsd: new Map(currentState.callTurnoverUsd),
  putTurnoverUsd: new Map(currentState.putTurnoverUsd),
  tickerMapById: new Map(currentState.tickerMapById),
});

// Utility: Process a single ticker update into the state updates object
const processTickerUpdate = (
  tickerData: TickerData,
  stateUpdates: Required<TickerState>,
  product: Partial<Product>,
  spotSymbol: string | null
): void => {
  const { contract_type } = product;
  const productId = tickerData?.product_id as number;

  const tickerItem = {
    open: tickerData?.open,
    high: tickerData?.high,
    low: tickerData?.low,
    turnover_usd: tickerData?.turnover_usd,
    turnover_inr: tickerData?.turnover_inr,
    turnover: tickerData?.turnover,
    volume: tickerData?.volume,
    close: tickerData?.close,
    product_id: tickerData?.product_id,
    funding_rate: tickerData?.funding_rate,
    symbol: tickerData?.symbol,
    contract_type: tickerData?.contract_type,
  };

  // Update tickerObj (by symbol)
  stateUpdates.tickerObj[tickerData?.symbol as string] = tickerItem;

  // Update optionsTickerObj (by symbol) for options contracts
  if (isCallPutOptions(contract_type)) {
    stateUpdates.optionsTickerObj[tickerData?.symbol as string] = tickerItem;
  }

  // Update tickerMapById (by product_id)
  stateUpdates.tickerMapById.set(productId, tickerItem);

  // Update spotPrice
  if (spotSymbol) {
    stateUpdates.spotPrice[spotSymbol] = Number(tickerData?.spot_price);
  }

  // Update markPrice
  stateUpdates.markPrice.set(productId, Number(tickerData?.mark_price));

  // Update priceBands
  if (tickerData?.price_band) {
    stateUpdates.priceBands.set(productId, tickerData.price_band);
  }

  // Update greeks
  if (tickerData?.greeks) {
    stateUpdates.greeks.delta.set(productId, tickerData.greeks.delta);
    stateUpdates.greeks.gamma.set(productId, tickerData.greeks.gamma);
    stateUpdates.greeks.theta.set(productId, tickerData.greeks.theta);
    stateUpdates.greeks.vega.set(productId, tickerData.greeks.vega);
  }

  // Update OI
  if (tickerData?.oi_value_usd) {
    stateUpdates.oi.set(productId, tickerData.oi_value_usd);

    if (contract_type === ContractType.CallOptions) {
      stateUpdates.callOi.set(productId, tickerData.oi_value_usd);
    }
    if (contract_type === ContractType.PutOptions) {
      stateUpdates.putOi.set(productId, tickerData.oi_value_usd);
    }
  }

  // Update OI change
  if (tickerData?.oi_change_usd_6h) {
    stateUpdates.oiChange.set(productId, tickerData.oi_change_usd_6h);
  }

  // Update quotes
  if (tickerData?.quotes) {
    stateUpdates.askIV.set(productId, Number(tickerData.quotes.ask_iv));
    stateUpdates.askQty.set(productId, Number(tickerData.quotes.ask_size));
    stateUpdates.bidIV.set(productId, Number(tickerData.quotes.bid_iv));
    stateUpdates.bidQty.set(productId, Number(tickerData.quotes.bid_size));
    stateUpdates.bestAsk.set(productId, Number(tickerData.quotes.best_ask));
    stateUpdates.bestBuy.set(productId, Number(tickerData.quotes.best_bid));
    stateUpdates.markIV.set(productId, Number(tickerData.quotes.mark_iv));
  }

  // Update implied volatility
  stateUpdates.impliedVolatility.set(productId, Number(tickerData.mark_vol));

  // Update turnover
  if (tickerData?.turnover_usd) {
    if (contract_type === ContractType.CallOptions) {
      stateUpdates.callTurnoverUsd.set(productId, tickerData.turnover_usd);
    }
    if (contract_type === ContractType.PutOptions) {
      stateUpdates.putTurnoverUsd.set(productId, tickerData.turnover_usd);
    }
  }
};

export { initializeStateUpdates, processTickerUpdate };
